<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { type StorefrontSettings } from '../composables/useStorefrontApi'
import { MapPinIcon, PhoneIcon, EnvelopeIcon } from '@heroicons/vue/24/outline'
import { useSettingsStore } from '../stores/settings'

const { t } = useI18n()
const currentYear = new Date().getFullYear()
const settingsStore = useSettingsStore()
const settings = computed(() => settingsStore.settings)
</script>

<template>
  <footer class="bg-gray-900 border-t border-gray-800 text-gray-300">
    <!-- Main Footer -->
    <div class="container mx-auto px-4 py-16">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <!-- About -->
        <div>
          <div class="flex items-center gap-3 mb-6">
            <div v-if="settings?.logo" class="h-10 w-auto max-w-[150px] flex items-center">
              <img 
                :src="settings.logo" 
                :alt="settings?.storeName || 'Logo'"
                class="h-full w-auto object-contain"
              />
            </div>
            <div v-else class="flex items-center gap-2.5">
              <div class="w-10 h-10 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl flex items-center justify-center shadow-lg">
                <span class="text-white text-xl font-bold">⚡</span>
              </div>
              <span class="text-xl font-bold text-white">{{ settings?.storeName || settings?.webshop?.storeName || 'Click Electra' }}</span>
            </div>
          </div>
          <p class="text-sm leading-relaxed mb-6 text-gray-400">
            {{ t('footer.description') }}
          </p>
          <div class="flex gap-3">
            <a v-if="settings?.webshop?.socialMedia?.facebook" :href="settings.webshop.socialMedia.facebook" target="_blank" class="w-10 h-10 bg-gray-800 hover:bg-primary-600 rounded-xl flex items-center justify-center transition text-white border border-gray-700 hover:border-primary-500">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
            <a v-if="settings?.webshop?.socialMedia?.instagram" :href="settings.webshop.socialMedia.instagram" target="_blank" class="w-10 h-10 bg-gray-800 hover:bg-pink-600 rounded-xl flex items-center justify-center transition text-white border border-gray-700 hover:border-pink-500">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9M18,5A1.25,1.25 0 0,1 19.25,6.25A1.25,1.25 0 0,1 18,7.5A1.25,1.25 0 0,1 16.75,6.25A1.25,1.25 0 0,1 18,5Z" />
              </svg>
            </a>
            <a v-if="settings?.webshop?.socialMedia?.twitter" :href="settings.webshop.socialMedia.twitter" target="_blank" class="w-10 h-10 bg-gray-800 hover:bg-blue-400 rounded-xl flex items-center justify-center transition text-white border border-gray-700 hover:border-blue-400">
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
              </svg>
            </a>
          </div>
        </div>

        <!-- Quick Links -->
        <div>
          <h3 class="text-white font-bold mb-6 text-lg">{{ t('footer.quickLinks') }}</h3>
          <ul class="space-y-3">
             <li><router-link to="/products" class="hover:text-primary-400 transition flex items-center gap-2 text-gray-400">
              {{ t('footer.allProducts') }}
            </router-link></li>
             <li><router-link to="/about" class="hover:text-primary-400 transition flex items-center gap-2 text-gray-400">
              {{ t('nav.about') }}
            </router-link></li>
            <li><router-link to="/contact" class="hover:text-primary-400 transition flex items-center gap-2 text-gray-400">
              {{ t('footer.contact') }}
            </router-link></li>
             <li><router-link to="/track-service" class="hover:text-primary-400 transition flex items-center gap-2 text-gray-400">
              {{ t('nav.trackOrder') }}
            </router-link></li>
          </ul>
        </div>

        <!-- Help / Customer Service -->
        <div>
          <h3 class="text-white font-bold mb-6 text-lg">{{ t('footer.help') }}</h3>
          <ul class="space-y-3">
            <li><router-link to="/delivery" class="hover:text-primary-400 transition flex items-center gap-2 text-gray-400">
              {{ t('footer.delivery') }}
            </router-link></li>
            <li><router-link to="/returns" class="hover:text-primary-400 transition flex items-center gap-2 text-gray-400">
              {{ t('footer.returns') }}
            </router-link></li>
            <li><router-link to="/terms" class="hover:text-primary-400 transition flex items-center gap-2 text-gray-400">
              {{ t('footer.terms') }}
            </router-link></li>
            <li><router-link to="/privacy" class="hover:text-primary-400 transition flex items-center gap-2 text-gray-400">
              {{ t('footer.privacy') }}
            </router-link></li>
          </ul>
        </div>

        <!-- Contact -->
        <div>
          <h3 class="text-white font-bold mb-6 text-lg">{{ t('footer.contactInfo') }}</h3>
          <ul class="space-y-4">
            <li v-if="settings" class="flex items-start gap-3 text-gray-400">
              <MapPinIcon class="w-5 h-5 text-primary-500 mt-1 flex-shrink-0" />
              <span class="text-sm leading-relaxed">{{ settings.address }}<br>{{ settings.city }} {{ settings.postalCode }}, {{ settings.country }}</span>
            </li>
            <li v-else class="flex items-start gap-3 text-gray-400">
              <MapPinIcon class="w-5 h-5 text-primary-500 mt-1 flex-shrink-0" />
              <span class="text-sm leading-relaxed">123 Tech Street<br>Skopje 1000, North Macedonia</span>
            </li>
            <li v-if="settings" class="flex items-center gap-3 text-gray-400">
              <PhoneIcon class="w-5 h-5 text-primary-500 flex-shrink-0" />
              <span class="text-sm font-medium">{{ settings.webshop.contactPhone || settings.phone }}</span>
            </li>
            <li v-else class="flex items-center gap-3 text-gray-400">
              <PhoneIcon class="w-5 h-5 text-primary-500 flex-shrink-0" />
              <span class="text-sm font-medium">+389 2 123 4567</span>
            </li>
            <li v-if="settings" class="flex items-center gap-3 text-gray-400">
              <EnvelopeIcon class="w-5 h-5 text-primary-500 flex-shrink-0" />
              <span class="text-sm">{{ settings.webshop.contactEmail || settings.email }}</span>
            </li>
            <li v-else class="flex items-center gap-3 text-gray-400">
              <EnvelopeIcon class="w-5 h-5 text-primary-500 flex-shrink-0" />
              <span class="text-sm">info@store.mk</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Copyright & Payment -->
    <div class="border-t border-gray-800 bg-gray-950">
      <div class="container mx-auto px-4 py-8">
        <div class="flex flex-col md:flex-row justify-between items-center gap-6 text-sm">
          <p class="text-gray-500">&copy; {{ currentYear }} {{ settings?.storeName || settings?.webshop?.storeName || 'Click Electra' }}. {{ t('footer.allRightsReserved') }}</p>
          
          <!-- Payment Icons -->
          <div class="flex items-center gap-4">
            <div class="h-8 w-12 bg-white/5 border border-white/10 rounded px-2 flex items-center justify-center transition opacity-60 hover:opacity-100" title="Visa">
              <img src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" alt="Visa" class="h-3 w-auto brightness-0 invert" />
            </div>
            <div class="h-8 w-12 bg-white/5 border border-white/10 rounded px-2 flex items-center justify-center transition opacity-60 hover:opacity-100" title="Mastercard">
              <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" class="h-5 w-auto brightness-0 invert" />
            </div>
            <div class="h-8 w-12 bg-white/5 border border-white/10 rounded px-2 flex items-center justify-center transition opacity-60 hover:opacity-100" title="Maestro">
              <img src="https://upload.wikimedia.org/wikipedia/commons/f/fd/Maestro_logo.svg" alt="Maestro" class="h-5 w-auto brightness-0 invert" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>
